import { useShallow } from 'zustand/react/shallow';
import { useDndMonitor } from '@dnd-kit/core';
import { type SelectedNode, useGallerySelection } from '@/context/GallerySelectionContext';
import { useBatchUpdateAssets } from '@/hooks/data/assets/useBatchUpdateAssets';
import { galleryChildDropId } from '@/hooks/dragDrop/useGalleryChildDropTarget';
import { AddAssetCommand } from '@/hooks/commands/albums/AddAssetCommand';
import { MoveNodeCommand } from '@/hooks/commands/folder/MoveNodeCommand';
import { ModalId, useOpenModal } from '@/hooks/data/modal/useModalStore';
import { useMoveNodesMutation } from '@/hooks/tree/useMoveNodesMutation';
import { useBrowserStore } from '@/hooks/data/browser/useBrowserStore';
import { useAlbumAssets } from '@/hooks/data/albums/useAlbumAssets';
import { useTreeStore } from '@/hooks/data/tree/useTreeStore';
import { useCommandContext } from '@/context/CommandContext';
import { ObjectAction, ObjectType } from '@/types/batch';
import { useTreeContext } from '@/context/TreeContext';
import { ToastVariant } from '@/components/ui/toast';
import { useToast } from '@/components/ui/use-toast';
import { error as errorLog } from '@/utilities/log';
import { DraggableItems } from '@/types/dragDrop';
import { DAMNodeType } from '@/types/tree';
import { useTranslations } from 'next-intl';

/**
 * Handles a drop onto any child folder/album in the browser view's leading section.
 *
 * Mounted once for the whole section rather than once per row: the drop target a row needs is
 * `useDroppable`, but the drag-end handler needs the mutation, translation and context stack
 * below, and at the section's cap that was all of it repeated a dozen times over and re-run on
 * every asset click. The target is resolved from `over.id`, which already carries the node id.
 *
 * Outcomes mirror the sidebar tree's ASSET-only drop branch, including what happens *after* the
 * mutation — the toast, the selection reset and `setUpdatedIds`. Those were missing here, which is
 * what made a completed move look like a copy that had done nothing.
 */
export const useGalleryChildrenDropMonitor = (children: Array<SelectedNode>) => {
  const { selectedAssets, deselectAllItems } = useGallerySelection();
  const { isAllSelected, setIsAllSelected } = useBrowserStore(
    useShallow((state) => ({ isAllSelected: state.isAllSelected, setIsAllSelected: state.setIsAllSelected })),
  );
  // setSelectedNodes/setUpdatedIds are what the sidebar tree's own move uses to reset the older
  // selection store and mark the destination for refresh.
  const { includeSubFolderAssets, setSelectedNodes, setUpdatedIds } = useTreeContext();
  const selectedNode = useTreeStore((state) => state.selectedNode);
  const { addAssetsMutation, removeAssetsMutation } = useAlbumAssets();
  const { moveNodes } = useMoveNodesMutation();
  const { batchUpdateAssetsMutation } = useBatchUpdateAssets();
  const { apply } = useCommandContext();
  const { toast } = useToast();
  const openModal = useOpenModal();
  const tToasts = useTranslations('toasts');
  const tDialogs = useTranslations('dialogs');
  const tCommon = useTranslations('common');

  useDndMonitor({
    onDragEnd(event) {
      void (async () => {
        const { active, over } = event;

        const node = children.find((child) => galleryChildDropId(child.id) === over?.id);

        if (
          !node ||
          !over?.data.current?.accepts?.includes(active.data.current?.type) ||
          active?.data?.current?.type !== DraggableItems.ASSET
        ) {
          return;
        }

        const isAlbum = node.type === DAMNodeType.Album;

        if (isAllSelected) {
          openModal(ModalId.Confirm, {
            content: tDialogs.rich('confirmMoveOrAddAllAssets', {
              type: isAlbum ? 'album' : 'folder',
              bold: (chunks) => <span className="font-bold">{chunks}</span>,
            }),
            confirmButtonVariant: 'success',
            confirmButtonLabel: isAlbum ? tCommon('add') : tCommon('move'),
            confirmButtonDataCy: 'gallery-child-move-all-assets-confirm',
            dismissButtonDataCy: 'gallery-child-move-all-assets-cancel',
            onConfirm: async () => {
              try {
                await batchUpdateAssetsMutation({
                  objectType: isAlbum ? ObjectType.Album : ObjectType.Node,
                  objectAction: isAlbum ? ObjectAction.Album_Asset_Update : ObjectAction.Move,
                  filter: {
                    ...(selectedNode?.type === DAMNodeType.Album
                      ? { albumId: active.data.current?.parentId }
                      : { folderId: active.data.current?.parentId, includeSubFolderAssets }),
                  },
                  payload: isAlbum ? { albumId: node.id, add: true } : { parentFolderNodeId: node.id },
                });

                toast({
                  title: tToasts(isAlbum ? 'asset.added.title' : 'asset.moved.title'),
                  description: tToasts(isAlbum ? 'asset.added.description' : 'asset.moved.description', {
                    name: node.name,
                  }),
                });

                deselectAllItems();
                setIsAllSelected(false);
              } catch (error) {
                errorLog('Action failed:', error);
                toast({
                  title: tToasts(isAlbum ? 'asset.addFailed.title' : 'asset.moveFailed.title'),
                  variant: ToastVariant.DESTRUCTIVE,
                  description: tToasts(isAlbum ? 'asset.addFailed.description' : 'asset.moveFailed.description', {
                    name: node.name,
                  }),
                });
              }
            },
          });
          return;
        }

        let assetIds: Array<string> = [active.data?.current?.id];
        if (selectedAssets.length > 1) {
          assetIds = selectedAssets.map((asset) => asset.id);
        }

        if (isAlbum) {
          // useAlbumAssets toasts from inside its own mutations, so adding to an album needs
          // nothing further here — same as the sidebar tree's album drop.
          const addAssetCommand = AddAssetCommand(addAssetsMutation, removeAssetsMutation, node.id, assetIds);
          await apply(addAssetCommand);
          return;
        }

        const moveNodeCommand = MoveNodeCommand(
          moveNodes,
          active.data?.current?.parentId,
          assetIds,
          DraggableItems.ASSET,
          node.id,
          // Suppressed so a failure produces one tailored toast below rather than the generic one.
          { suppressGlobalErrorToast: true },
        );

        /*
         * Everything after `apply` mirrors the sidebar tree's own asset-move branch
         * (components/sidebar/tree/folder.tsx). Dropping onto a child had the mutation and nothing
         * else: no success toast, no selection reset, and no `setUpdatedIds`, so the move landed
         * server-side while the UI sat unchanged — the asset still showing in the folder it had just
         * left, which reads as a copy. `suppressGlobalErrorToast` also made failures completely
         * silent, since nothing caught them either.
         */
        try {
          await apply(moveNodeCommand);

          toast({
            title: tToasts('assetMove.title'),
            description: tToasts('assetMove.description', { count: assetIds.length }),
          });

          setSelectedNodes([]);
          deselectAllItems();
          setIsAllSelected(false);
          setUpdatedIds([node.id]);
        } catch (error) {
          errorLog('Moving assets onto a child folder failed:', error);
          const status =
            typeof (error as { status?: unknown }).status === 'number'
              ? (error as { status: number }).status
              : undefined;

          toast({
            title: tToasts('move.failed.title'),
            variant: ToastVariant.DESTRUCTIVE,
            // 409 is a name collision in the destination, which is the failure worth naming.
            description:
              status === 409
                ? tToasts.rich('move.failed.conflictMultiple', {
                    destination: node.name,
                    destinationTag: (chunks) => <span className="font-bold">{chunks}</span>,
                  })
                : tToasts('move.failed.description'),
          });
        }
      })();
    },
  });
};
